import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Wallet, TrendingUp, ArrowDownToLine, Plus, Trash2, Edit3,
  Users, ChevronDown, ChevronRight, CalendarDays, AlertCircle,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmtCurrency = (v, ccy = 'EUR') => {
  if (v === null || v === undefined) return '—';
  try {
    return new Intl.NumberFormat('el-GR', {
      style: 'currency',
      currency: ccy || 'EUR',
      maximumFractionDigits: 0,
    }).format(v);
  } catch {
    return `${v} ${ccy}`;
  }
};

const STATUS_COLORS = {
  committed: 'bg-blue-100 text-blue-700 border-blue-200',
  active: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  completed: 'bg-slate-200 text-slate-700 border-slate-300',
  cancelled: 'bg-rose-100 text-rose-700 border-rose-200',
};

/**
 * FundingLedger — Investor ↔ Project commitment + drawdown ledger.
 *
 * Renders a summary card (total committed / drawn / remaining) and one
 * row per commitment with progress bar and expandable drawdown history.
 * Admin/PM users see action buttons to add/edit/delete commitments and
 * record/remove drawdowns. Investors see only their own row, read-only.
 */
const FundingLedger = ({ projectId, currentUser }) => {
  const role = currentUser?.role;
  const canManage = role === 'administrator' || role === 'project_manager';

  const [loading, setLoading] = useState(true);
  const [commitments, setCommitments] = useState([]);
  const [summary, setSummary] = useState(null);
  const [investorOptions, setInvestorOptions] = useState([]);
  const [expanded, setExpanded] = useState({});

  // Dialog states
  const [commitDialog, setCommitDialog] = useState({ open: false, editing: null });
  const [drawDialog, setDrawDialog] = useState({ open: false, commitmentId: null });
  const [confirmDel, setConfirmDel] = useState({ open: false, id: null });

  const auth = useMemo(
    () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }),
    []
  );

  const fetchData = async () => {
    setLoading(true);
    try {
      const [c, s] = await Promise.all([
        axios.get(`${API}/projects/${projectId}/funding`, auth),
        axios.get(`${API}/projects/${projectId}/funding/summary`, auth),
      ]);
      setCommitments(c.data || []);
      setSummary(s.data || null);
    } catch (err) {
      const code = err?.response?.status;
      if (code === 403) {
        setCommitments([]);
        setSummary(null);
      } else {
        toast.error('Failed to load funding ledger');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchInvestors = async () => {
    if (!canManage) return;
    try {
      const { data } = await axios.get(`${API}/social-investors`, auth);
      setInvestorOptions(
        (data || []).map((p) => ({
          user_id: p.user_id,
          label: `${p.organization_name || 'Unnamed'} (${p.investor_type || 'investor'})`,
        }))
      );
    } catch {
      /* non-fatal */
    }
  };

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!projectId) return;
      setLoading(true);
      try {
        const [c, s] = await Promise.all([
          axios.get(`${API}/projects/${projectId}/funding`, auth),
          axios.get(`${API}/projects/${projectId}/funding/summary`, auth),
        ]);
        if (cancelled) return;
        setCommitments(c.data || []);
        setSummary(s.data || null);
      } catch (err) {
        if (cancelled) return;
        if (err?.response?.status === 403) {
          setCommitments([]);
          setSummary(null);
        } else {
          toast.error('Failed to load funding ledger');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
      if (canManage) {
        try {
          const { data } = await axios.get(`${API}/social-investors`, auth);
          if (!cancelled) {
            setInvestorOptions(
              (data || []).map((p) => ({
                user_id: p.user_id,
                label: `${p.organization_name || 'Unnamed'} (${p.investor_type || 'investor'})`,
              }))
            );
          }
        } catch { /* non-fatal */ }
      }
    };
    run();
    return () => { cancelled = true; };
  }, [projectId, auth, canManage]);

  const toggleExpand = (id) =>
    setExpanded((p) => ({ ...p, [id]: !p[id] }));

  // ---------- Commitment create/edit ----------
  const submitCommitment = async (form) => {
    try {
      if (commitDialog.editing) {
        const { id } = commitDialog.editing;
        await axios.patch(`${API}/projects/${projectId}/funding/${id}`, {
          committed_amount: Number(form.committed_amount),
          currency: form.currency,
          commitment_date: form.commitment_date,
          status: form.status,
          notes: form.notes,
        }, auth);
        toast.success('Commitment updated');
      } else {
        await axios.post(`${API}/projects/${projectId}/funding`, {
          investor_id: form.investor_id,
          committed_amount: Number(form.committed_amount),
          currency: form.currency,
          commitment_date: form.commitment_date,
          status: form.status,
          notes: form.notes,
        }, auth);
        toast.success('Commitment recorded');
      }
      setCommitDialog({ open: false, editing: null });
      await fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save commitment');
    }
  };

  const deleteCommitment = async () => {
    const id = confirmDel.id;
    try {
      await axios.delete(`${API}/projects/${projectId}/funding/${id}`, auth);
      toast.success('Commitment deleted');
      setConfirmDel({ open: false, id: null });
      await fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to delete');
    }
  };

  // ---------- Drawdown record ----------
  const submitDrawdown = async (form) => {
    try {
      await axios.post(
        `${API}/projects/${projectId}/funding/${drawDialog.commitmentId}/drawdowns`,
        {
          amount: Number(form.amount),
          date: form.date,
          reference: form.reference,
          note: form.note,
        }, auth);
      toast.success('Drawdown recorded');
      setDrawDialog({ open: false, commitmentId: null });
      await fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to record drawdown');
    }
  };

  const removeDrawdown = async (commitmentId, drawdownId) => {
    try {
      await axios.delete(
        `${API}/projects/${projectId}/funding/${commitmentId}/drawdowns/${drawdownId}`,
        auth
      );
      toast.success('Drawdown removed');
      await fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to remove drawdown');
    }
  };

  // ---------- Render ----------
  if (loading) {
    return (
      <div className="py-12 text-center text-slate-500" data-testid="funding-loading">
        Loading funding ledger…
      </div>
    );
  }

  const ccy = summary?.currency || commitments[0]?.currency || 'EUR';

  return (
    <div className="space-y-6" data-testid="funding-ledger">
      {/* Header + add button */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
            <Wallet className="h-5 w-5 text-emerald-700" /> Funding Ledger
          </h2>
          <p className="text-sm text-slate-500">
            Investor commitments and drawdowns against this project.
          </p>
        </div>
        {canManage && (
          <Button
            onClick={() => setCommitDialog({ open: true, editing: null })}
            className="bg-emerald-700 hover:bg-emerald-800 text-white"
            data-testid="add-commitment-btn"
          >
            <Plus className="h-4 w-4 mr-1" /> Add commitment
          </Button>
        )}
      </div>

      {/* Summary */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card data-testid="summary-committed">
            <CardContent className="p-4">
              <div className="text-xs uppercase tracking-wider text-slate-500">Total committed</div>
              <div className="text-2xl font-bold text-slate-900 mt-1">
                {fmtCurrency(summary.total_committed, ccy)}
              </div>
              <div className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                <Users className="h-3 w-3" /> {summary.investor_count} investor(s) ·
                {' '}{summary.commitment_count} commitment(s)
              </div>
            </CardContent>
          </Card>
          <Card data-testid="summary-drawn">
            <CardContent className="p-4">
              <div className="text-xs uppercase tracking-wider text-slate-500">Drawn</div>
              <div className="text-2xl font-bold text-blue-700 mt-1">
                {fmtCurrency(summary.total_drawn, ccy)}
              </div>
              <div className="text-xs text-slate-500 mt-1">{summary.progress_pct}% of commitments</div>
            </CardContent>
          </Card>
          <Card data-testid="summary-remaining">
            <CardContent className="p-4">
              <div className="text-xs uppercase tracking-wider text-slate-500">Remaining</div>
              <div className="text-2xl font-bold text-emerald-700 mt-1">
                {fmtCurrency(summary.remaining, ccy)}
              </div>
              <div className="text-xs text-slate-500 mt-1">Available for drawdown</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-xs uppercase tracking-wider text-slate-500">Progress</div>
              <div className="mt-2">
                <div className="h-3 rounded-full bg-slate-100 overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-blue-500 to-emerald-500"
                    style={{ width: `${Math.min(summary.progress_pct, 100)}%` }}
                  />
                </div>
                <div className="text-xs text-slate-500 mt-1.5">{summary.progress_pct}% drawn</div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Empty state */}
      {commitments.length === 0 && (
        <Card>
          <CardContent className="py-10 text-center">
            <AlertCircle className="h-8 w-8 text-slate-300 mx-auto mb-2" />
            <p className="text-slate-500">No funding commitments recorded yet.</p>
            {canManage && (
              <Button
                variant="outline"
                onClick={() => setCommitDialog({ open: true, editing: null })}
                className="mt-3"
                data-testid="add-commitment-empty-btn"
              >
                <Plus className="h-4 w-4 mr-1" /> Add the first commitment
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Commitments list */}
      {commitments.map((c) => (
        <Card key={c.id} data-testid={`commitment-${c.id}`}>
          <CardHeader className="pb-2">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <CardTitle className="text-base flex items-center gap-2">
                  <span className="truncate">{c.investor_org_name || 'Unknown investor'}</span>
                  <Badge variant="outline" className={STATUS_COLORS[c.status] || 'bg-slate-100'}>
                    {c.status}
                  </Badge>
                </CardTitle>
                <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-3 flex-wrap">
                  <span className="flex items-center gap-1">
                    <CalendarDays className="h-3 w-3" /> Since {c.commitment_date}
                  </span>
                  {c.investor_username && (
                    <span className="text-slate-500">@{c.investor_username}</span>
                  )}
                </div>
              </div>
              {canManage && (
                <div className="flex items-center gap-1 shrink-0">
                  <Button
                    size="sm" variant="ghost"
                    onClick={() => setDrawDialog({ open: true, commitmentId: c.id })}
                    data-testid={`add-drawdown-${c.id}`}
                  >
                    <ArrowDownToLine className="h-4 w-4 mr-1" /> Record drawdown
                  </Button>
                  <Button
                    size="sm" variant="ghost"
                    onClick={() => setCommitDialog({ open: true, editing: c })}
                    data-testid={`edit-commitment-${c.id}`}
                  >
                    <Edit3 className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm" variant="ghost"
                    onClick={() => setConfirmDel({ open: true, id: c.id })}
                    data-testid={`delete-commitment-${c.id}`}
                    className="text-rose-600 hover:text-rose-700"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-3 mb-3">
              <div>
                <div className="text-xs text-slate-500">Committed</div>
                <div className="font-semibold text-slate-900">
                  {fmtCurrency(c.committed_amount, c.currency)}
                </div>
              </div>
              <div>
                <div className="text-xs text-slate-500">Drawn</div>
                <div className="font-semibold text-blue-700">
                  {fmtCurrency(c.drawn_amount, c.currency)}
                </div>
              </div>
              <div>
                <div className="text-xs text-slate-500">Remaining</div>
                <div className="font-semibold text-emerald-700">
                  {fmtCurrency(c.remaining_amount, c.currency)}
                </div>
              </div>
            </div>

            <div className="h-2 rounded-full bg-slate-100 overflow-hidden mb-3">
              <div
                className="h-full bg-gradient-to-r from-blue-500 to-emerald-500"
                style={{ width: `${Math.min(c.progress_pct, 100)}%` }}
              />
            </div>
            <div className="text-xs text-slate-500 mb-3">{c.progress_pct}% drawn</div>

            {c.notes && (
              <p className="text-sm text-slate-600 italic mb-3">&ldquo;{c.notes}&rdquo;</p>
            )}

            {/* Drawdowns toggle */}
            <button
              type="button"
              onClick={() => toggleExpand(c.id)}
              className="text-xs text-slate-700 hover:text-slate-900 inline-flex items-center gap-1 font-medium"
              data-testid={`toggle-drawdowns-${c.id}`}
            >
              {expanded[c.id] ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
              {(c.drawdowns || []).length} drawdown(s)
            </button>

            {expanded[c.id] && (c.drawdowns || []).length > 0 && (
              <div className="mt-3 border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
                    <tr>
                      <th className="px-3 py-2 text-left">Date</th>
                      <th className="px-3 py-2 text-right">Amount</th>
                      <th className="px-3 py-2 text-left">Reference</th>
                      <th className="px-3 py-2 text-left">Note</th>
                      {canManage && <th className="px-3 py-2 w-10"></th>}
                    </tr>
                  </thead>
                  <tbody>
                    {c.drawdowns.map((d) => (
                      <tr key={d.id} className="border-t" data-testid={`drawdown-${d.id}`}>
                        <td className="px-3 py-2 text-slate-700">{d.date}</td>
                        <td className="px-3 py-2 text-right font-medium text-blue-700">
                          {fmtCurrency(d.amount, c.currency)}
                        </td>
                        <td className="px-3 py-2 text-slate-600">{d.reference || '—'}</td>
                        <td className="px-3 py-2 text-slate-500 truncate max-w-xs">{d.note || '—'}</td>
                        {canManage && (
                          <td className="px-3 py-2">
                            <button
                              onClick={() => removeDrawdown(c.id, d.id)}
                              className="text-rose-600 hover:text-rose-700"
                              data-testid={`remove-drawdown-${d.id}`}
                              aria-label="Remove drawdown"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      ))}

      {/* Commitment dialog — remount on open/edit-target change to reset form */}
      <CommitmentDialog
        key={`commit-${commitDialog.editing?.id || 'new'}-${commitDialog.open}`}
        open={commitDialog.open}
        editing={commitDialog.editing}
        investorOptions={investorOptions}
        onClose={() => setCommitDialog({ open: false, editing: null })}
        onSubmit={submitCommitment}
      />
      {/* Drawdown dialog — remount on each open to reset form */}
      <DrawdownDialog
        key={`draw-${drawDialog.commitmentId || 'none'}-${drawDialog.open}`}
        open={drawDialog.open}
        commitment={commitments.find((c) => c.id === drawDialog.commitmentId)}
        onClose={() => setDrawDialog({ open: false, commitmentId: null })}
        onSubmit={submitDrawdown}
      />
      {/* Confirm delete */}
      <Dialog open={confirmDel.open} onOpenChange={(o) => !o && setConfirmDel({ open: false, id: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete commitment?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            This will permanently remove the commitment and all its drawdowns from
            the project&apos;s funding ledger. This action cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDel({ open: false, id: null })}>
              Cancel
            </Button>
            <Button
              onClick={deleteCommitment}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="confirm-delete-commitment-btn"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ───────────────────── Commitment Dialog ─────────────────────
const CommitmentDialog = ({ open, editing, investorOptions, onClose, onSubmit }) => {
  // Reset form whenever the dialog opens by remounting via key (see parent).
  const [form, setForm] = useState(() => editing ? {
    investor_id: editing.investor_id,
    committed_amount: editing.committed_amount,
    currency: editing.currency || 'EUR',
    commitment_date: editing.commitment_date || new Date().toISOString().slice(0, 10),
    status: editing.status || 'active',
    notes: editing.notes || '',
  } : {
    investor_id: '',
    committed_amount: '',
    currency: 'EUR',
    commitment_date: new Date().toISOString().slice(0, 10),
    status: 'active',
    notes: '',
  });

  const valid = (editing || form.investor_id) && Number(form.committed_amount) > 0;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{editing ? 'Edit commitment' : 'Add commitment'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {!editing && (
            <div>
              <Label>Investor</Label>
              <Select
                value={form.investor_id}
                onValueChange={(v) => setForm({ ...form, investor_id: v })}
              >
                <SelectTrigger data-testid="commitment-investor-select">
                  <SelectValue placeholder="Select an investor" />
                </SelectTrigger>
                <SelectContent>
                  {investorOptions.map((o) => (
                    <SelectItem key={o.user_id} value={o.user_id}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Committed amount</Label>
              <Input
                type="number" min="0" step="1000"
                value={form.committed_amount}
                onChange={(e) => setForm({ ...form, committed_amount: e.target.value })}
                data-testid="commitment-amount-input"
              />
            </div>
            <div>
              <Label>Currency</Label>
              <Select
                value={form.currency}
                onValueChange={(v) => setForm({ ...form, currency: v })}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="EUR">EUR</SelectItem>
                  <SelectItem value="USD">USD</SelectItem>
                  <SelectItem value="GBP">GBP</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Commitment date</Label>
              <Input
                type="date"
                value={form.commitment_date}
                onChange={(e) => setForm({ ...form, commitment_date: e.target.value })}
              />
            </div>
            <div>
              <Label>Status</Label>
              <Select
                value={form.status}
                onValueChange={(v) => setForm({ ...form, status: v })}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="committed">Committed</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Notes</Label>
            <Textarea
              rows={2}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            disabled={!valid}
            onClick={() => onSubmit(form)}
            data-testid="commitment-submit-btn"
          >
            {editing ? 'Save changes' : 'Add commitment'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ───────────────────── Drawdown Dialog ─────────────────────
const DrawdownDialog = ({ open, commitment, onClose, onSubmit }) => {
  // Form initialized once per mount; parent remounts via key on open.
  const [form, setForm] = useState(() => ({
    amount: '', date: new Date().toISOString().slice(0, 10), reference: '', note: '',
  }));

  const remaining = commitment?.remaining_amount ?? 0;
  const tooLarge = Number(form.amount) > remaining + 0.01;
  const valid = Number(form.amount) > 0 && !tooLarge;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Record drawdown</DialogTitle>
        </DialogHeader>
        {commitment && (
          <div className="text-xs text-slate-500 -mt-2 mb-1">
            Against <span className="font-medium text-slate-700">{commitment.investor_org_name}</span> ·
            Remaining {fmtCurrency(remaining, commitment.currency)}
          </div>
        )}
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Amount</Label>
              <Input
                type="number" min="0" step="100"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                data-testid="drawdown-amount-input"
              />
              {tooLarge && (
                <p className="text-xs text-rose-600 mt-1">Exceeds remaining commitment.</p>
              )}
            </div>
            <div>
              <Label>Date</Label>
              <Input
                type="date"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
              />
            </div>
          </div>
          <div>
            <Label>Reference</Label>
            <Input
              placeholder="e.g. Tranche 3 / Invoice TR-2025-Q3"
              value={form.reference}
              onChange={(e) => setForm({ ...form, reference: e.target.value })}
            />
          </div>
          <div>
            <Label>Note</Label>
            <Textarea
              rows={2}
              value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            disabled={!valid}
            onClick={() => onSubmit(form)}
            data-testid="drawdown-submit-btn"
          >
            <ArrowDownToLine className="h-4 w-4 mr-1" /> Record
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default FundingLedger;
